import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { designDetails, images } from "../content/site";
import { FadeUp } from "./Reveal";

function ParallaxPanel({ src, label, tag, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const offset = index % 2 === 0 ? "lg:translate-y-0" : "lg:translate-y-16";

  return (
    <FadeUp y={50} className={offset}>
      <figure ref={ref} className="group relative overflow-hidden">
        <div className="aspect-[3/4] overflow-hidden">
          <motion.img
            src={src}
            alt={label}
            loading="lazy"
            style={{ y }}
            className="h-[120%] w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-ink/80 to-transparent p-6">
          <span className="font-serif text-xl text-cream">{label}</span>
          <span className="font-sans text-[10px] uppercase tracking-overline text-brass">{tag}</span>
        </figcaption>
      </figure>
    </FadeUp>
  );
}

export default function DesignDetails() {
  const srcByKey = {
    wood: images.detail.wood,
    navy: images.detail.navy,
    steel: images.detail.steel,
    light: images.detail.light,
  };

  return (
    <section
      id="design"
      data-testid="section-design-details"
      className="relative bg-cream py-28 sm:py-36"
    >
      <div className="container-edge">
        <div className="max-w-2xl">
          <FadeUp delay={0.05}>
            <h2 className="font-serif text-4xl leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              {designDetails.headline}
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-8 font-sans text-base font-light leading-relaxed text-navy/70">
              {designDetails.body}
            </p>
          </FadeUp>
        </div>

        <div className="mt-20 grid grid-cols-2 gap-5 sm:gap-8 lg:grid-cols-4">
          {designDetails.panels.map((p, i) => (
            <ParallaxPanel
              key={p.key}
              src={srcByKey[p.key]}
              label={p.label}
              tag={p.tag}
              index={i}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
